import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { useEffect, useState } from 'react';
import {
    CompleteOrderProvider,
    GetAllProviderVehicleListDown,
    GetOrderDetails,
} from '../../../../reduxToolkit/Providers/providersApi';
import { PersonChipBlack, VehicleChip } from '../../../../components/buttons/IconButtons';
import { userListToLabelEmailPhoneValue, vehicleListToLabel } from '../../../../Apis/functions';
import { ETA_MODEL } from '../Model/Model';
import HandleChange from '../../../../Helpers/HandleChange';

/**
 * @Email : azapata@ibl.mx
 * @Author: Ferro Zapata Arnulfo
 * @jobTittle: Full Stack Developer
 /**
 * @param {boolean} isUpdateOrder
 *   Indicates whether the current operation is updating an existing order (true) or creating a new one (false).
 *
 * @returns {{
 *   handleEtaDateChange: function(Date): void,
 *   employeeName: string,
 *   handelCompleteOrder: function(): void,
 *   vehicleDataTransform: function(any): any,
 *   setEmployeeName: function(string | (function(string): string)): void,
 *   vehicleBrand: string,
 *   getAllProviderVehicleListDown: any,
 *   getOrderDetails: any,
 *   emptyFields: boolean,
 *   t: TFunction<"translation", undefined>,
 *   setVehicleBrand: function(string | (function(string): string)): void,
 *   setIsValidDate: function(boolean | (function(boolean): boolean)): void,
 *   etaDate: unknown
 * }}
 *
 * @description
 * Returns an object that manages the completion of an order, including data handling for provider employees and vehicles,
 * ETA (Estimated Time of Arrival) date management, validation flags, and field setters for employee and vehicle info.
 *
 * - `handleEtaDateChange`: Updates the ETA date.
 * - `employeeName`: Name of the selected employee related to the order.
 * - `handelCompleteOrder`: Completes and submits the current order.
 * - `vehicleDataTransform`: Transforms raw vehicle data into the desired format.
 * - `setEmployeeName`: Sets the employee name (string or updater function).
 * - `vehicleBrand`: Selected vehicle brand name.
 * - `getAllProviderVehicleListDown`: Provider vehicle list retrieval function or data source.
 * - `getOrderDetails`: Function or object to retrieve full order details.
 * - `emptyFields`: Boolean flag indicating if required fields are missing.
 * - `t`: i18n translation function.
 * - `setVehicleBrand`: Sets the vehicle brand name (string or updater function).
 * - `setIsValidDate`: Sets whether the current ETA date is valid.
 * - `etaDate`: Current ETA date value.
 *
 * @constructor
 */

export const CompleteOrderApp = ({ isUpdateOrder }) => {
    const { t } = useTranslation();
    const { id } = useParams();
    const dispatch = useDispatch();
    const [emptyFields, setEmptyFields] = useState(false);
    const [selectedList, setSelectedList] = useState([]);
    const [selectedListVehicle, setSelectedListVehicle] = useState([]);
    const [item, setItem] = useState({ ...ETA_MODEL });
    const [vehicleBrand, setVehicleBrand] = useState('');
    const [modified, setModified] = useState(false);
    const { getAllProviderVehicleListDown } = useSelector((state) => state.providersSlice);
    const { getOrderDetails } = useSelector((state) => state.providersSlice);
    const [isValidDate, setIsValidDate] = useState(true);
    const { handleChange } = HandleChange({ model: item, setModel: setItem });

    // Completes the order by validating required fields
    const handelCompleteOrder = () => {
        if (
            selectedList?.length === 0 ||
            selectedListVehicle?.length === 0 ||
            !item.estimatedTimeOfArrivalDate ||
            !isValidDate
        ) {
            setEmptyFields(true);
            return;
        }
        const updatedItem = {
            ...item,
            eta: Math.round(item.estimatedTimeOfArrivalDate),
            userId: selectedList[0]?.id,
            vehicleId: selectedListVehicle[0]?.id,
        };

        const orderId = getOrderDetails?.id;

        dispatch(CompleteOrderProvider({ data: updatedItem, orderId })).then(
            ({
                payload: {
                    data: { data, success },
                },
            }) => {
                if (success) {
                    setModified(true);
                }
            },
        );

        setEmptyFields(false);
    };

    //syncs data when order changes or update mode is enabled
    useEffect(() => {
        if (isUpdateOrder) {
            setVehicleBrand(getOrderDetails?.supplierVehicle?.vehicle?.id || '');
            setItem((prev) => ({
                ...prev,
                estimatedTimeOfArrivalDate: getOrderDetails?.eta || null,
            }));
        }
    }, [getOrderDetails?.id, isUpdateOrder]);

    //fetch order details when component mounts or id changes
    useEffect(() => {
        dispatch(GetOrderDetails(id)).then(
            ({
                payload: {
                    data: { data, success },
                },
            }) => {
                if (success) {
                    setItem({
                        estimatedTimeOfArrivalDate: data?.eta ? new Date(Number(data.eta)) : null,
                        userId: null,
                        vehicleId: null,
                    });
                    dispatch(GetAllProviderVehicleListDown(data?.supplier?.id));
                    const users = Array.isArray(data?.supplierEmployee?.user)
                        ? data.supplierEmployee.user
                        : data?.supplierEmployee?.user
                          ? [data.supplierEmployee.user]
                          : [];

                    const transformedUsers = userListToLabelEmailPhoneValue(users, 'id', true, PersonChipBlack);

                    setSelectedList(transformedUsers);
                    const vehicles = Array.isArray(data?.supplierVehicle?.vehicle)
                        ? data.supplierVehicle.vehicle
                        : data?.supplierVehicle?.vehicle
                          ? [data.supplierVehicle.vehicle]
                          : [];

                    const transformedVehicles = vehicleListToLabel(vehicles, 'id', true, VehicleChip);
                    setSelectedListVehicle(transformedVehicles);
                }
            },
        );
    }, [id, dispatch, modified]);

    // Handles ETA date change while ensuring year consistency
    const handleEstimatedTimeOfArrivalDateChange = (date) => {
        if (date) {
            const year = date.getFullYear();
            const adjustedYear = year < 100 ? year + 2000 : year;
            const newDate = new Date(date);
            newDate.setFullYear(adjustedYear);

            setItem((prev) => ({
                ...prev,
                estimatedTimeOfArrivalDate: newDate,
            }));
        } else {
            setItem((prev) => ({
                ...prev,
                estimatedTimeOfArrivalDate: null,
            }));
        }
    };

    return {
        t,
        handelCompleteOrder,
        getOrderDetails,
        estimatedTimeOfArrivalDate: item.estimatedTimeOfArrivalDate,
        handleEstimatedTimeOfArrivalDateChange,
        emptyFields,
        setIsValidDate,
        selectedList,
        setSelectedList,
        setVehicleBrand,
        vehicleBrand,
        getAllProviderVehicleListDown,
        selectedListVehicle,
        setSelectedListVehicle,
        id,
        handleChange,
        item,
    };
};
